<template>
    <KvPopup class="kv-date-picker" :placement="placement" @close-outside="handleClose" ref="datePicker">
        <template #trigger="{ toggle }">
            <div class="kv-date-picker-trigger" @click="!disabled && toggle()">
                <slot
                    name="trigger"
                    :toggle="toggle"
                    :formatted-date="formattedDate"
                    :value="value"
                    :disabled="disabled"
                >
                    <KvInput
                        :value="formattedDate"
                        :label="label"
                        :placeholder="placeholder"
                        :variant="variant"
                        :size="size"
                        :disabled="disabled"
                        :readonly="true"
                        :has-error="hasError"
                        class="kv-date-trigger"
                        prepend-icon="ui-calendar"
                    />
                </slot>
            </div>
        </template>

        <template #content="{ toggle }">
            <div class="kv-picker-content">
                <div class="kv-calendar-header">
                    <KvButton prepend-icon="ui-chevron-left" @click="changeMonth(-1)" variant="text" size="small"></KvButton>

                    <div class="d-flex align-items-center gap-sm">
                        <KvSelect :style="{ width: '70px' }" variant="tinted" size="small" :value="calendar.month" :options="monthOptions" :clearable="false" value-field="value" label-field="label" :placeholder="t('inputs-datepicker-month')" @input="(val) => handleHeaderChange('month', val)" />

                        <KvInput :style="{ width: '80px' }" variant="tinted" size="small" :value="calendar.year" type="number" :placeholder="t('inputs-datepicker-year')" @input="(val) => handleHeaderChange('year', val)" />
                    </div>

                    <KvButton prepend-icon="ui-chevron-right" @click="changeMonth(1)" variant="text" size="small"></KvButton>
                </div>

                <div class="kv-calendar-weekdays">
                    <span v-for="d in daysOfWeek" :key="d">{{ d }}</span>
                </div>

                <div class="kv-calendar-days">
                    <span v-for="day in calendar.days" :key="day.toISODate()" :class="{
                        'kv-day': true,
                        'kv-outside': day.month !== calendar.month,
                        'kv-selected': isSelected(day),
                        'kv-today': isToday(day)
                    }" @click="selectDay(day, toggle)">
                        {{ day.day }}
                    </span>
                </div>

                <div v-if="type === 'datetime'" class="kv-time-picker">
                    <div class="time-label">{{ t('inputs-datepicker-time') }}</div>
                    <div class="time-inputs">
                        <KvInput type="number" variant="tinted" size="small" :value="timeParts.hour" :min="0" :max="23" :placeholder="t('inputs-datepicker-hour_placeholder')" @input="(val) => updateTime('hour', val)" class="time-input" />
                        <span class="time-separator">:</span>
                        <KvInput type="number" variant="tinted" size="small" :value="timeParts.minute" :min="0" :max="59" :placeholder="t('inputs-datepicker-minute_placeholder')" @input="(val) => updateTime('minute', val)" class="time-input" />
                    </div>
                </div>

                <div class="kv-picker-footer">
                    <KvButton size="small" variant="text" @click="selectToday(toggle)">
                        {{ type === 'datetime' ? t('inputs-datepicker-now') : t('inputs-datepicker-today') }}
                    </KvButton>

                    <div class="kv-picker-footer-actions">
                        <KvButton
                            v-if="clearable && internalDate"
                            size="small"
                            variant="text"
                            @click="clearSelection(toggle)"
                        >
                            {{ t('inputs-datepicker-clear') }}
                        </KvButton>
                        <KvButton v-if="type === 'datetime'" size="small" variant="primary" @click="confirmSelection(toggle)">
                            {{ t('inputs-datepicker-confirm') }}
                        </KvButton>
                    </div>
                </div>
            </div>
        </template>
    </KvPopup>
</template>

<script>
import { DateTime, Info } from "luxon";
import KvPopup from "@/components/ui/kv-popup.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvInput from "@/components/inputs/kv-input.vue";
import KvSelect from "@/components/inputs/kv-select.vue";
import Icon from "../icon/icon.vue";

export default {
    name: "KvDatePicker",
    components: { KvPopup, KvButton, KvInput, KvSelect, Icon },
    props: {
        value: {
            required: false,
        },
        label: {
            type: String,
            default: ""
        },
        placeholder: {
            type: String,
            default: ""
        },
        variant: {
            type: String,
            default: "tonal"
        },
        size: {
            type: String,
            default: "regular"
        },
        disabled: {
            type: Boolean,
            default: false
        },
        hasError: {
            type: Boolean,
            default: false
        },
        format: {
            type: String,
            default: null
        },
        valueFormat: {
            type: String,
            default: null
        },
        inputFormat: {
            type: String,
            default: null,
        },
        type: {
            type: String,
            default: "date",
            validator: (value) => ["date", "datetime"].includes(value)
        },
        placement: {
            type: String,
            default: "bottom right",
        },
        clearable: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        const today = DateTime.now();
        return {
            internalDate: null,
            viewDate: today,
            today,
        };
    },
    mounted() {
        if (this.internalDate && this.internalDate.isValid) {
            this.emitValue();
        }
    },
    computed: {
        daysOfWeek() {
            return [
                this.t('inputs-datepicker-weekdays-sun'),
                this.t('inputs-datepicker-weekdays-mon'),
                this.t('inputs-datepicker-weekdays-tue'),
                this.t('inputs-datepicker-weekdays-wed'),
                this.t('inputs-datepicker-weekdays-thu'),
                this.t('inputs-datepicker-weekdays-fri'),
                this.t('inputs-datepicker-weekdays-sat'),
            ];
        },
        monthOptions() {
            return [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((index) => {
                const label = this.t(`dates-full_months-${index}`);
                return {
                    label: (label.charAt(0).toUpperCase() + label.slice(1)).slice(0, 3),
                    value: index + 1
                };
            });
        },
        displayFormat() {
            if (this.format) return this.format;
            return this.type === 'datetime' ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy";
        },
        formattedDate() {
            if (!this.internalDate) return "";
            return this.internalDate.toFormat(this.displayFormat);
        },
        calendar() {
            return this.buildCalendar(this.viewDate);
        },
        timeParts() {
            if (!this.internalDate) return { hour: '00', minute: '00' };
            return {
                hour: this.internalDate.toFormat('HH'),
                minute: this.internalDate.toFormat('mm')
            };
        }
    },
    watch: {
        value: {
            immediate: true,
            handler(val) {
                const dt = this.toDateTime(val);
                this.internalDate = dt;
                if (dt && dt.isValid) {
                    this.viewDate = dt;
                }
            }
        }
    },
    methods: {
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        toDateTime(value) {
            if (!value) return null;

            if (value instanceof DateTime) return value;
            if (value instanceof Date) return DateTime.fromJSDate(value);

            if (typeof value === "string") {
                let dt;

                if (this.inputFormat) {
                    dt = DateTime.fromFormat(value, this.inputFormat, { locale: 'en-US' });
                    if (dt.isValid) return dt;

                    dt = DateTime.fromFormat(value, this.inputFormat, { locale: 'pt-BR' });
                    if (dt.isValid) return dt;
                }

                dt = DateTime.fromISO(value);
                if (dt.isValid) return dt;

                dt = DateTime.fromRFC2822(value);
                if (dt.isValid) return dt;

                dt = DateTime.fromSQL(value);
                if (dt.isValid) return dt;

                dt = DateTime.fromFormat(value, this.displayFormat);
                if (dt.isValid) return dt;

                dt = DateTime.fromFormat(value, "yyyy-MM-dd HH:mm:ss");
                if (dt.isValid) return dt;

                dt = DateTime.fromFormat(value, "dd/MM/yyyy");
                if (dt.isValid) return dt;
            }

            return null;
        },
        buildCalendar(base) {
            const startOfMonth = base.startOf("month");
            const endOfMonth = base.endOf("month");

            const startOffset = startOfMonth.weekday % 7;
            const endOffset = 6 - (endOfMonth.weekday % 7);

            const start = startOfMonth.minus({ days: startOffset });
            const end = endOfMonth.plus({ days: endOffset });
            const days = [];
            let current = start;
            while (current <= end) {
                days.push(current);
                current = current.plus({ days: 1 });
            }
            return {
                month: base.month,
                year: base.year,
                days,
            };
        },
        changeMonth(dir) {
            this.viewDate = this.viewDate.plus({ months: dir });
        },
        handleHeaderChange(type, value) {
            const val = parseInt(value);
            if (!val) return;
            if (type === "year" && val.toString().length !== 4) return;

            this.viewDate = this.viewDate.set({ [type]: val });
        },
        selectDay(day, toggleFn) {
            let newDate = day;

            if (this.internalDate) {
                newDate = day.set({
                    hour: this.internalDate.hour,
                    minute: this.internalDate.minute,
                    second: 0
                });
            } else if (this.type === 'datetime') {
                const now = DateTime.now();
                newDate = day.set({ hour: now.hour, minute: now.minute });
            }

            this.internalDate = newDate;
            this.emitValue();

            if (this.type === 'date') {
                this.closePopup(toggleFn);
            }
        },
        updateTime(unit, val) {
            let value = parseInt(val);
            if (isNaN(value)) value = 0;

            if (unit === 'hour') {
                if (value > 23) value = 23;
                if (value < 0) value = 0;
            } else {
                if (value > 59) value = 59;
                if (value < 0) value = 0;
            }

            if (!this.internalDate) {
                this.internalDate = DateTime.now().startOf('day');
                this.viewDate = this.internalDate;
            }

            this.internalDate = this.internalDate.set({ [unit]: value });
            this.emitValue();
        },
        selectToday(toggleFn) {
            this.internalDate = this.today;
            this.viewDate = this.today;
            this.emitValue();

            this.closePopup(toggleFn);
        },
        confirmSelection(toggleFn) {
            this.closePopup(toggleFn);
        },
        clearSelection(toggleFn) {
            this.internalDate = null;
            this.$emit("input", null);
            this.$emit("change", null);
            this.closePopup(toggleFn);
        },
        closePopup(toggleFn) {
            if (toggleFn && typeof toggleFn === 'function') {
                toggleFn();
            } else {
                this.$refs.datePicker.$emit('close-outside');
            }
        },
        emitValue() {
            if (!this.internalDate) {
                this.$emit("input", null);
                return;
            }

            let output;

            if (this.valueFormat) {
                output = this.internalDate.setLocale('pt-BR').toFormat(this.valueFormat);
            } else {
                output = this.internalDate.toJSDate();
            }

            this.$emit("input", output);
            this.$emit("change", this.internalDate);
        },
        isSelected(day) {
            if (!this.internalDate) return false;
            return day.hasSame(this.internalDate, "day");
        },
        isToday(day) {
            return day.hasSame(this.today, "day");
        },
        handleClose() {
            if (this.internalDate) {
                this.viewDate = this.internalDate;
            } else {
                this.viewDate = this.today;
            }
        }
    }
};
</script>

<style lang="less">
.kv-date-picker {
    display: inline-block;
    width: 100%;

    .kv-date-trigger {
        cursor: pointer;

        .kv-native-input {
            cursor: pointer;
        }

        .kv-input-icon {
            color: #6b7280;
            display: flex;
            align-items: center;
            justify-content: center;
            padding-right: 2px;
        }
    }

    .kv-picker-content {
        padding: 12px;
        width: 280px;
        background: #fff;
        border-radius: 8px;

        .kv-calendar-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
        }

        .kv-calendar-weekdays {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            text-align: center;
            font-size: 11px;
            color: #6b7280;
            font-weight: 600;
            margin-bottom: 4px;
        }

        .kv-calendar-days {
            display: grid;
            grid-template-columns: repeat(7, 1fr);
            row-gap: 2px;

            .kv-day {
                display: flex;
                align-items: center;
                justify-content: center;
                height: 32px;
                width: 32px;
                font-size: 13px;
                cursor: pointer;
                user-select: none;
                color: #1f2a37;
                border-radius: 6px;
                margin: 0 auto;

                &:hover {
                    background: #F1FCF3;
                    color: #1D7836;
                }

                &.kv-outside {
                    color: #d1d5db;
                }

                &.kv-today {
                    font-weight: bold;
                    border: 1px solid #E5E7EB;
                }

                &.kv-selected {
                    background-color: #2AAA4C;
                    color: #FFF;
                    font-weight: 600;

                    &:hover {
                        background-color: darken(#2AAA4C, 5%);
                    }
                }
            }
        }

        .kv-time-picker {
            margin-top: 12px;
            padding-top: 10px;
            border-top: 1px solid #f3f4f6;
            display: flex;
            align-items: center;
            justify-content: space-between;

            .time-label {
                font-size: 13px;
                color: #4b5563;
                font-weight: 500;
            }

            .time-inputs {
                display: flex;
                align-items: center;
                gap: 4px;

                .time-separator {
                    font-weight: bold;
                    color: #6b7280;
                }

                .time-input {
                    width: 75px;

                    input[type=number]::-webkit-inner-spin-button,
                    input[type=number]::-webkit-outer-spin-button {
                        -webkit-appearance: none;
                        margin: 0;
                    }
                }
            }
        }

        .kv-picker-footer {
            margin-top: 10px;
            padding-top: 8px;
            border-top: 1px solid #f3f4f6;
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 8px;
        }

        .kv-picker-footer-actions {
            display: flex;
            align-items: center;
            gap: 4px;
            margin-left: auto;
        }
    }
}
</style>
